import Image from 'next/image';
import { type KeyboardEvent, memo, type ReactNode, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { BoardIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { type SelectableItem } from '@air/redux-selected-items';
import { formatBytes } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import canvasCardPlaceholder from '~/assets/canvas-card-placeholder.png';
import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { getAgentSelectionListItemId } from '~/components/v2/components/private-selection-bar/agent-selection-bar/hooks/useAgentSelectionListKeyboardNav';
import { useUnselectAgentSelectedItem } from '~/components/v2/components/private-selection-bar/agent-selection-bar/hooks/useUnselectAgentSelectedItem';
import { isSelectableGalleryItem } from '~/store/selectedItems/types';
import { ClipIconSize } from '~/types/clip-icon-size';

export type AgentSelectedItemProps = {
  allowUnselect?: boolean;
  id?: string;
  isSelected?: boolean;
  item: SelectableItem;
  listItemIndex?: number;
  onPointerEnter?: (listItemIndex: number) => void;
  shouldFocusUnselect?: boolean;
};

export type AgentSelectedItemRowProps = {
  allowUnselect: boolean;
  id?: string;
  isSelected: boolean;
  onPointerEnter?: () => void;
  onUnselect: () => void;
  shouldFocusUnselect?: boolean;
  subtitle: ReactNode;
  thumbnail: ReactNode;
  title: string;
  unselectLabel: string;
};

const AgentSelectedItemRow = memo(
  ({
    allowUnselect,
    id,
    isSelected,
    onPointerEnter,
    onUnselect,
    shouldFocusUnselect = false,
    subtitle,
    thumbnail,
    title,
    unselectLabel,
  }: AgentSelectedItemRowProps) => {
    const rowRef = useRef<HTMLDivElement>(null);

    const handleUnselectKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation();
      }
    }, []);

    useIsomorphicLayoutEffect(() => {
      if (!isSelected || !allowUnselect || !shouldFocusUnselect) {
        return;
      }

      rowRef.current?.querySelector('button')?.focus({ preventScroll: true });
    }, [allowUnselect, isSelected, shouldFocusUnselect]);

    return (
      <div
        aria-selected={isSelected}
        className={cn(
          'flex w-full flex-row gap-2 px-3 py-1.5 transition-colors hover:bg-white/5',
          isSelected && 'bg-white/10',
        )}
        data-testid="AGENT_SELECTION_LIST_ITEM"
        id={id}
        onPointerEnter={onPointerEnter}
        ref={rowRef}
        role="option"
        tabIndex={-1}
      >
        {thumbnail}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
          <div className="truncate font-medium text-14 text-white/90">{title}</div>
          {subtitle}
        </div>
        <div className="ml-auto flex shrink-0 items-center justify-center">
          {allowUnselect && (
            <IconButton
              adsIcon
              appearance="ghost"
              className="shrink-0 bg-transparent text-white/50 hover:bg-white/10 hover:text-white"
              color="gray"
              label={unselectLabel}
              onClick={onUnselect}
              onKeyDown={handleUnselectKeyDown}
              size="small"
              tabIndex={isSelected ? 0 : -1}
              icon={XIcon}
            />
          )}
        </div>
      </div>
    );
  },
);

AgentSelectedItemRow.displayName = 'AgentSelectedItemRow';

export const AgentSelectedItem = memo(
  ({
    allowUnselect = true,
    id,
    isSelected = false,
    item,
    listItemIndex,
    onPointerEnter,
    shouldFocusUnselect = false,
  }: AgentSelectedItemProps) => {
    const { unselectAgentSelectedItem } = useUnselectAgentSelectedItem(item.id);

    const handlePointerEnter = useCallback(() => {
      if (listItemIndex !== undefined) {
        onPointerEnter?.(listItemIndex);
      }
    }, [listItemIndex, onPointerEnter]);

    const rowId = id ?? (listItemIndex !== undefined ? getAgentSelectionListItemId(listItemIndex) : undefined);

    if (!isSelectableGalleryItem(item)) {
      return null;
    }

    if (item.type === 'board' || item.type === 'topLevelBoard') {
      const board = item.item;

      return (
        <AgentSelectedItemRow
          allowUnselect={allowUnselect}
          id={rowId}
          isSelected={isSelected}
          onPointerEnter={handlePointerEnter}
          onUnselect={unselectAgentSelectedItem}
          shouldFocusUnselect={shouldFocusUnselect}
          subtitle={
            <div className="flex items-center gap-1 font-medium text-10 text-white/45 uppercase">
              <BoardIcon size="xs" aria-hidden className="shrink-0 text-white/45" />
              Board
            </div>
          }
          thumbnail={
            <div className="shrink-0 overflow-hidden rounded-8 bg-white/10 p-1">
              <BoardThumbnail board={board} height={40} objectFit="cover" sizes="100vw" width={72} />
            </div>
          }
          title={board.title}
          unselectLabel={`Unselect ${board.title}`}
        />
      );
    }

    if (item.type === 'asset' || item.type === 'file') {
      const clip = item.item;
      const clipTitle = clip.title || clip.importedName || 'Untitled';
      const metadata = [clip.ext, formatBytes(clip.size)].join(' · ');

      return (
        <AgentSelectedItemRow
          allowUnselect={allowUnselect}
          id={rowId}
          isSelected={isSelected}
          onPointerEnter={handlePointerEnter}
          onUnselect={unselectAgentSelectedItem}
          shouldFocusUnselect={shouldFocusUnselect}
          subtitle={<div className="flex gap-1 truncate font-medium text-10 text-white/45 uppercase">{metadata}</div>}
          thumbnail={
            <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-8 bg-white/10">
              <ClipThumbnail
                className="size-full rounded-8 object-cover"
                description={clip.description}
                ext={clip.ext}
                height={48}
                iconSize={ClipIconSize.large}
                importedName={clip.importedName}
                size="mediumSmall"
                src={clip.assets.image}
                status={clip.status}
                type={clip.type}
                width={80}
              />
            </div>
          }
          title={clipTitle}
          unselectLabel={`Unselect ${clipTitle}`}
        />
      );
    }

    if (item.type === 'canvas') {
      const canvas = item.item;
      const canvasTitle = canvas.title || 'Untitled';

      return (
        <AgentSelectedItemRow
          allowUnselect={allowUnselect}
          id={rowId}
          isSelected={isSelected}
          onPointerEnter={handlePointerEnter}
          onUnselect={unselectAgentSelectedItem}
          shouldFocusUnselect={shouldFocusUnselect}
          subtitle={<div className="flex gap-1 truncate font-medium text-10 text-white/45 uppercase">Canvas</div>}
          thumbnail={
            <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-8 bg-white/10">
              <Image
                alt=""
                className="size-full object-cover object-center"
                height={48}
                src={canvasCardPlaceholder.src}
                width={80}
              />
            </div>
          }
          title={canvasTitle}
          unselectLabel={`Unselect ${canvasTitle}`}
        />
      );
    }

    return null;
  },
);

AgentSelectedItem.displayName = 'AgentSelectedItem';
